/**
 * How a leg's API endpoint is DISPLAYED, and where the value came from.
 *
 * WHY THIS FILE EXISTS
 *
 * `JobDetail` gated its endpoint row on `job.outbound_method === 'eapi'` and rendered
 * `job.api_base_url`. That column is an **eFax-only JOB column**, so:
 *
 *   - an SFax or SRFax job showed NO endpoint at all while genuinely dialling one, and
 *   - an eFax leg naming an `efax_api` provider account does not read that column either
 *     (`resolve_leg` treats a named account as authoritative), so the column could be
 *     present, populated by its model default, and NOT the host in use.
 *
 * This is the same shape as `outboundHandle.ts`: a value whose provider or origin is not
 * stated invites the wrong reading, and eFax's regional endpoints differ only in their TLD —
 * SOV credentials against the wrong region answer `401 Bad credentials`, which reads as bad
 * credentials rather than as a wrong endpoint.
 *
 * SO THE VERDICT IS COMPUTED SERVER-SIDE AND THIS FILE ONLY PHRASES IT. The precedence is
 * `resolve_leg`'s (named account, then the job's own column where the leg declares the field,
 * then the endpoint compiled in for that provider) and re-deriving it in the browser is how two
 * surfaces come to disagree about what a job dials — the reason `leg_account_health` is
 * server-side too. `backend/api/jobs.py::_leg_api_endpoint` produces it; the `status` strings
 * below are that function's own vocabulary.
 */

/** One leg's endpoint verdict, as `GET /api/jobs/{id}` returns it. */
export interface LegApiEndpoint {
  /** One of the `API_ENDPOINT_*` values below. */
  status: string
  /** The FQDN in use. Null on every status except `account` / `job` / `provider_default`. */
  url?: string | null
  /** The leg's provider slug, resolved the way the send path resolves it. */
  provider?: string | null
  /** False when the provider column was NULL and the default provider was applied. */
  provider_recorded?: boolean
  /** The endpoints this provider may be dialled at, compiled into the application. */
  candidates?: string[]
  /** Operator-facing sentence naming the source, or why there is none. */
  detail?: string | null
}

/** This leg dials no HTTP API — SMTP send, IMAP poll, or a callback we receive. */
export const API_ENDPOINT_NOT_APPLICABLE = 'not_applicable'
/** The leg names an account and the response did not load the account rows. */
export const API_ENDPOINT_NOT_EVALUATED = 'not_evaluated'
/** From the named provider account's `api_base_url`. */
export const API_ENDPOINT_ACCOUNT = 'account'
/** From the job's own `api_base_url` column. */
export const API_ENDPOINT_JOB = 'job'
/** From the endpoint compiled in for this leg's own provider. */
export const API_ENDPOINT_PROVIDER_DEFAULT = 'provider_default'
/** No endpoint could be established. NEVER rendered as a URL. */
export const API_ENDPOINT_UNKNOWN = 'unknown'

/**
 * Whether this leg's endpoint row should render at all.
 *
 * `not_applicable` is the one status that renders NOTHING, and that is deliberate rather than
 * tidiness: the eAPI endpoint is meaningless on an SMTP job, nothing in the SMTP send path
 * touches an API, and the row used to appear whenever the column held a value — which it does
 * on every job — so SMTP jobs advertised an API they never call.
 *
 * Every other status DOES render, `unknown` included. Naming the gap is the point of this
 * change: a leg the send path cannot resolve is a real state an operator has to see, and a
 * blank row says nothing while looking like a working one.
 */
export function showsApiEndpoint(endpoint: LegApiEndpoint | null | undefined): boolean {
  if (!endpoint) return false
  return endpoint.status !== API_ENDPOINT_NOT_APPLICABLE
}

/** What a status with no establishable endpoint reads as. */
const NOT_ESTABLISHED = 'not established'

/**
 * The short WHERE-FROM tag shown beside the FQDN.
 *
 * The source travels with the value wherever there is a source to name: a URL on its own
 * cannot be checked against anything, and an operator diagnosing a wrong endpoint needs to
 * know which screen holds it.
 *
 * `provider_default` is the ONE status with no tag, and deliberately. Nothing holds it —
 * there is no screen to send anyone to — so the tag could only restate what the absence of
 * a configured source already says, on the row an operator reads most often. An empty
 * string rather than a special case at the call site, so a status that has nothing to say
 * says nothing rather than saying something empty.
 */
export function apiEndpointSourceText(endpoint: LegApiEndpoint): string {
  switch (endpoint.status) {
    case API_ENDPOINT_ACCOUNT:
      return 'from the provider account'
    case API_ENDPOINT_JOB:
      return 'from this job'
    case API_ENDPOINT_PROVIDER_DEFAULT:
      return ''
    case API_ENDPOINT_NOT_EVALUATED:
      return `${NOT_ESTABLISHED} — account records not loaded`
    default:
      return NOT_ESTABLISHED
  }
}

/**
 * What the row's value reads as: the FQDN plus its source, or the explicit no-endpoint state.
 *
 * NEVER a URL for a status that carries none, and never a brand's default substituted for a
 * leg that supplied nothing — that is the `outboundHandle.ts` rule. A leg whose endpoint could
 * not be established says so in the value, not by being empty.
 *
 * The no-URL branch keeps a subject even for a status with no source tag. `⚠️` alone is a
 * warning about nothing, which is worse than the suffix that was dropped above: a
 * compiled-in default that produced no URL is genuinely not established, and the row says
 * that rather than trailing off.
 */
export function apiEndpointText(endpoint: LegApiEndpoint): string {
  const source = apiEndpointSourceText(endpoint)
  if (!endpoint.url) return `⚠️ ${source || NOT_ESTABLISHED}`
  return source ? `${endpoint.url} (${source})` : endpoint.url
}
